import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Inbox, SearchX } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  H2,
  Input,
  Mono,
  PageHeader,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stat,
  Text,
  useDebouncedValue,
} from "@lagoon/ui";

import {
  api,
  type EntitlementOverride,
  type Me,
  type OrgBilling,
  type OrgDetail,
  type OrgSearchRow,
} from "../api.js";
import { AccountStateBadge, SiteStateBadge } from "../components/badges.js";
import { ReasonAction } from "../components/reason-action.js";
import { formatBytes, formatCount } from "../format.js";
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS } from "../search.js";

type Member = OrgDetail["members"][number];
type OrgSite = OrgDetail["sites"][number];
type BillingEvent = OrgBilling["events"][number];
type OverrideHistoryRow = OrgBilling["history"][number];

/**
 * Organizations, which is where the commercial questions land.
 *
 * The site screen answers "what is this site doing"; this one answers "who is
 * this account, what are they paying for, and are they in good standing". They
 * are separate screens because they are separate questions asked by different
 * people at different moments, and one screen carrying both would serve neither.
 */
export function OrgsScreen({ me }: { me: Me }): React.JSX.Element {
  const [term, setTerm] = useState("");
  const trimmed = term.trim();
  const settled = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  // A half-typed term is not a query, so it browses rather than searching for
  // one character. The screen used to render nothing at all until the second
  // character arrived, which made an empty console look broken.
  const query = settled.length >= MIN_SEARCH_LENGTH ? settled : "";

  const results = useQuery({
    queryKey: ["orgs", query],
    queryFn: () => api.orgs(query),
  });

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        eyebrow="Tenants"
        title="Organisations"
        description="Accounts, their plans, and whether they are in good standing."
        {...(me.can.administer ? { actions: <ProvisionOrg /> } : {})}
      />

      <div className="lg:max-w-xl">
        <SearchInput
          value={term}
          onValueChange={setTerm}
          label="Search organisations"
          placeholder="organisation name, member email, or site slug"
          autoFocus
        />
        {trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH && (
          <Text size="caption" tone="subtle" className="lg:mt-2">
            Keep typing, at least two characters. Showing the newest accounts meanwhile.
          </Text>
        )}
      </div>

      {results.error && (
        <Text size="sm" tone="danger" role="alert">
          {results.error.message}
        </Text>
      )}

      {results.data && (
        <div className="lg:flex lg:flex-col lg:gap-2">
          <DataTable
            columns={ORG_COLUMNS}
            data={results.data.orgs}
            loading={results.isFetching}
            caption={
              query === "" ? "The newest organisations" : "Organisations matching the search"
            }
            empty={<EmptyState icon={<SearchX />} title="No organisations match" glow={false} />}
          />
          <Text size="caption" tone="subtle">
            {query === "" ? "The newest accounts. " : ""}
            {formatCount(results.data.orgs.length, results.data.total, "organisation")}
          </Text>
        </div>
      )}

      {!me.can.administer && (
        <Text size="sm" tone="muted">
          Provisioning an organisation is an administrator action, because it creates an account and
          emails somebody outside the company.
        </Text>
      )}
    </div>
  );
}

const ORG_COLUMNS: DataTableColumn<OrgSearchRow>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: (info) => (
      <a href={`#/org/${info.row.original.id}`} className="lg:text-primary lg:hover:underline">
        {info.row.original.name}
      </a>
    ),
  },
  {
    accessorKey: "kind",
    header: "Kind",
    cell: (info) => <Badge tone="quiet">{info.row.original.kind}</Badge>,
  },
  { accessorKey: "plan", header: "Plan" },
  {
    accessorKey: "memberCount",
    header: "Members",
    cell: (info) => <Mono>{info.row.original.memberCount.toLocaleString()}</Mono>,
  },
  {
    accessorKey: "siteCount",
    header: "Sites",
    cell: (info) => <Mono>{info.row.original.siteCount.toLocaleString()}</Mono>,
  },
  {
    id: "state",
    header: "State",
    cell: (info) => <AccountStateBadge suspendedAt={info.row.original.suspendedAt} />,
  },
  {
    // The column the default listing is ordered by. A list sorted by something
    // it does not show is a list nobody can check.
    accessorKey: "createdAt",
    header: "Created",
    sortFn: "datetime",
    cell: (info) => (
      <Mono className="lg:text-caption lg:text-muted-foreground">
        {new Date(info.row.original.createdAt).toLocaleDateString()}
      </Mono>
    ),
  },
];

/**
 * Provision an organization for a design partner (plan CD6).
 *
 * Until self-serve signup opens, this is how an outside customer gets in.
 * Two steps like every other action on this surface, and the reason is the
 * field that says who asked for the account.
 */
function ProvisionOrg(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [plan, setPlan] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<{
    field: "name" | "email" | "reason";
    text: string;
  } | null>(null);
  const queryClient = useQueryClient();

  const run = useMutation({
    mutationFn: () =>
      api.provisionOrg({
        name: name.trim(),
        ownerEmail: ownerEmail.trim(),
        ...(plan.trim() ? { plan: plan.trim() } : {}),
        reason: reason.trim(),
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["orgs"] });
      setOpen(false);
      setName("");
      setOwnerEmail("");
      setPlan("");
      setReason("");
      setProblem(null);
      window.location.hash = `#/org/${created.id}`;
    },
  });

  const confirm = (): void => {
    if (name.trim().length === 0) {
      setProblem({ field: "name", text: "Give the organisation a name." });
      return;
    }
    if (!ownerEmail.includes("@")) {
      setProblem({ field: "email", text: "The first owner needs an address to be invited at." });
      return;
    }
    if (reason.trim().length < 3) {
      setProblem({ field: "reason", text: "Say who asked for this account." });
      return;
    }
    setProblem(null);
    run.mutate();
  };

  return (
    <>
      <Button
        onClick={() => {
          setOpen(true);
        }}
      >
        Provision an organisation
      </Button>

      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setProblem(null);
        }}
        title="Provision an organisation"
        description="Creates the organisation and its first owner, and emails them a sign-in link. The address does not need an account yet: signing in with it claims the one this creates."
        confirmLabel={run.isPending ? "Working…" : "Provision"}
        onConfirm={confirm}
        pending={run.isPending}
      >
        <div className="lg:flex lg:flex-col lg:gap-4">
          <FormField
            label="Organisation name"
            required
            {...(problem?.field === "name" ? { error: problem.text } : {})}
          >
            <Input
              value={name}
              autoFocus
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </FormField>

          <FormField
            label="First owner's email address"
            required
            {...(problem?.field === "email" ? { error: problem.text } : {})}
          >
            <Input
              type="email"
              value={ownerEmail}
              onChange={(event) => {
                setOwnerEmail(event.target.value);
              }}
            />
          </FormField>

          <FormField label="Plan" hint="Optional. Defaults to free.">
            <Input
              value={plan}
              onChange={(event) => {
                setPlan(event.target.value);
              }}
            />
          </FormField>

          <FormField
            label="Reason"
            required
            hint="Recorded in the audit trail."
            {...(problem?.field === "reason" ? { error: problem.text } : {})}
          >
            <Input
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </FormField>

          {run.error && (
            <Text size="sm" tone="danger" role="alert">
              {run.error.message}
            </Text>
          )}
          {run.data?.notified === false && (
            <Text size="sm" tone="danger" role="alert">
              The organisation was created but the invitation email did not send. Tell them
              yourself, and check the mail provider.
            </Text>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}

/** One organization: who is in it, what it owns, and what it may do. */
export function OrgScreen({ orgId, me }: { orgId: string; me: Me }): React.JSX.Element {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["org", orgId], queryFn: () => api.org(orgId) });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["org", orgId] });
    void queryClient.invalidateQueries({ queryKey: ["health"] });
  }, [queryClient, orgId]);

  if (detail.isPending) {
    return (
      <Text size="sm" tone="muted">
        Loading…
      </Text>
    );
  }
  if (detail.error) {
    return (
      <Text size="sm" tone="danger" role="alert">
        {detail.error.message}
      </Text>
    );
  }

  const { org, members, sites } = detail.data;
  const suspended = org.suspendedAt !== null;
  const published = sites.filter((site) => site.publishedDeployId !== null);

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        breadcrumb={
          <a href="#/orgs" className="lg:text-caption lg:text-muted-foreground lg:hover:underline">
            &larr; Organisations
          </a>
        }
        eyebrow="Organisation"
        title={org.name}
        description={`${org.kind} · plan ${org.plan} · ${String(org.seats)} seats`}
        actions={<AccountStateBadge suspendedAt={org.suspendedAt} />}
      />

      {suspended && org.suspendedReason !== null && (
        <Alert tone="danger">
          <AlertTitle>Suspended</AlertTitle>
          <AlertDescription>{org.suspendedReason}</AlertDescription>
        </Alert>
      )}

      <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:xl:grid-cols-5">
        <Stat value={sites.length.toLocaleString()} label="Sites" />
        <Stat value={published.length.toLocaleString()} label="Published" />
        <Stat value={org.quotaSites.toLocaleString()} label="Site quota" />
        <Stat
          value={formatBytes(org.quotaBytes)}
          label="Storage quota"
          className="lg:[&_[data-slot=stat-value]]:text-h3"
        />
        <Stat
          value={new Date(org.createdAt).toLocaleDateString()}
          label="Created"
          className="lg:[&_[data-slot=stat-value]]:text-h3"
        />
      </div>

      <BillingPanel orgId={orgId} me={me} />

      {me.can.operate ? (
        <section className="lg:flex lg:flex-col lg:gap-4">
          <H2 className="lg:text-h3">Actions</H2>
          <div className="lg:flex lg:flex-wrap lg:items-start lg:gap-3">
            <ReasonAction
              label={suspended ? "Unsuspend organisation" : "Suspend organisation"}
              danger={!suspended}
              hint={
                suspended
                  ? "Lifts the freeze, and unsuspends only the sites this suspension suspended. A site taken down on its own merits stays down."
                  : `Freezes the account and suspends its ${String(sites.length - published.length)} unpublished sites. The ${String(published.length)} published sites keep serving: taking those down is a separate step.`
              }
              onSubmit={async (why) => {
                await (suspended ? api.unsuspendOrg(orgId, why) : api.suspendOrg(orgId, why));
                invalidate();
              }}
            />
          </div>
        </section>
      ) : (
        <Alert tone="info">
          <AlertTitle>Auditor</AlertTitle>
          <AlertDescription>
            You are signed in as an auditor. This view is read-only for your account.
          </AlertDescription>
        </Alert>
      )}

      <section className="lg:flex lg:flex-col lg:gap-4">
        <H2 className="lg:text-h3">Members</H2>
        <DataTable columns={MEMBER_COLUMNS} data={members} caption="Members of this organisation" />
      </section>

      <section className="lg:flex lg:flex-col lg:gap-4">
        <H2 className="lg:text-h3">Sites</H2>
        <DataTable
          columns={ORG_SITE_COLUMNS}
          data={sites}
          caption="Sites this organisation owns"
          empty={
            <EmptyState
              icon={<Building2 />}
              title="This organisation has no sites"
              description="Nothing has been created under this account yet."
              glow={false}
            />
          }
        />
      </section>
    </div>
  );
}

/** Owner, admin, member. The word is the label; the tone is the reminder. */
function roleTone(role: string): "primary" | "info" | "neutral" {
  if (role === "owner") return "primary";
  if (role === "admin") return "info";
  return "neutral";
}

const MEMBER_COLUMNS: DataTableColumn<Member>[] = [
  {
    accessorKey: "email",
    header: "Email",
    cell: (info) => <Mono className="lg:text-strong">{info.row.original.email}</Mono>,
  },
  {
    accessorKey: "name",
    header: "Name",
    cell: (info) => info.row.original.name ?? <span className="lg:text-subtle">not set</span>,
  },
  {
    accessorKey: "role",
    header: "Role",
    cell: (info) => <Badge tone={roleTone(info.row.original.role)}>{info.row.original.role}</Badge>,
  },
  {
    id: "state",
    header: "State",
    cell: (info) => <AccountStateBadge suspendedAt={info.row.original.ownerSuspendedAt} />,
  },
];

const ORG_SITE_COLUMNS: DataTableColumn<OrgSite>[] = [
  {
    accessorKey: "slug",
    header: "Slug",
    cell: (info) => (
      <a
        href={`#/site/${info.row.original.id}`}
        className="lg:font-mono lg:text-primary lg:hover:underline"
      >
        {info.row.original.slug}
      </a>
    ),
  },
  { accessorKey: "name", header: "Name" },
  {
    accessorKey: "ownerEmail",
    header: "Owner",
    cell: (info) => (
      <Mono className="lg:text-muted-foreground">{info.row.original.ownerEmail}</Mono>
    ),
  },
  {
    id: "state",
    header: "State",
    cell: (info) => (
      <SiteStateBadge
        suspendedAt={info.row.original.suspendedAt}
        publishedDeployId={info.row.original.publishedDeployId}
        reason={info.row.original.suspendedReason}
      />
    ),
  },
];

/**
 * The entitlement keys an override may set, and how each one is edited.
 *
 * A copy of the vocabulary `packages/domain/src/entitlements.ts` defines, and
 * deliberately only a copy: the server refuses a key it does not know
 * (`parseEntitlementLayer`), so this list decides what the form offers and
 * decides nothing about what is accepted. A key added to the platform and
 * forgotten here is invisible in the console and still safe.
 */
const DEFAULT_OVERRIDE_KEY = "quota_sites";

const OVERRIDABLE: { key: string; label: string; kind: "number" | "boolean" }[] = [
  { key: DEFAULT_OVERRIDE_KEY, label: "Sites", kind: "number" },
  { key: "quota_bytes", label: "Storage (bytes)", kind: "number" },
  { key: "max_seats", label: "Seats", kind: "number" },
  { key: "max_viewers", label: "Viewers per month", kind: "number" },
  { key: "otp_daily_budget", label: "Passcode emails per day", kind: "number" },
  { key: "retention_days", label: "Deploy retention (days)", kind: "number" },
  { key: "custom_domains_included", label: "Custom domains included", kind: "number" },
  { key: "audit_export", label: "Audit export", kind: "boolean" },
  { key: "custom_domains", label: "Custom domains", kind: "boolean" },
  { key: "public_mode", label: "Public mode", kind: "boolean" },
  { key: "mcp_access", label: "MCP publishing", kind: "boolean" },
  { key: "sso", label: "SSO", kind: "boolean" },
];

const STATE_LABEL: Record<OrgBilling["state"], string> = {
  trialing: "Trialling",
  active: "Active",
  past_due: "Past due",
  delinquent: "Delinquent",
  ended: "Ended",
  unknown: "No subscription",
};

const STATE_TONE: Record<OrgBilling["state"], "success" | "danger" | "neutral"> = {
  trialing: "success",
  active: "success",
  past_due: "danger",
  delinquent: "danger",
  ended: "neutral",
  unknown: "neutral",
};

/**
 * Billing, as Tech Ops needs to see it.
 *
 * The panel answers three questions in the order somebody actually asks them:
 * what is this customer on, what has billing said about them lately, and has
 * anybody adjusted them by hand. The fourth question, "what does Maxio think",
 * is answered by a link rather than by an API call, because plan CD4 keeps
 * billing calls off request paths and because Maxio's own screen is better at
 * cards and invoices than a panel here would ever be.
 *
 * Loaded separately from the org detail rather than folded into it. This read
 * touches four tables that the rest of the org screen does not need, and an
 * operator opening an org to suspend a site should not wait on the billing
 * inbox to render the page.
 */
function BillingPanel({ orgId, me }: { orgId: string; me: Me }): React.JSX.Element {
  const queryClient = useQueryClient();
  const billing = useQuery({
    queryKey: ["org-billing", orgId],
    queryFn: () => api.orgBilling(orgId),
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["org-billing", orgId] });
    void queryClient.invalidateQueries({ queryKey: ["org", orgId] });
  }, [queryClient, orgId]);

  if (billing.isPending) {
    return (
      <Text size="sm" tone="muted">
        Loading billing…
      </Text>
    );
  }
  if (billing.error) {
    return (
      <Text size="sm" tone="danger" role="alert">
        {billing.error.message}
      </Text>
    );
  }

  const data = billing.data;
  const override = data.override;

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <H2 className="lg:text-h3">Billing</H2>

      <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-3">
        <Badge tone="quiet">{data.catalog?.name ?? data.plan}</Badge>
        <Badge tone={STATE_TONE[data.state]}>{STATE_LABEL[data.state]}</Badge>
        {data.stateAt !== null && (
          <Text size="caption" tone="subtle" as="span">
            last event {new Date(data.stateAt).toLocaleString()}
          </Text>
        )}
        {data.trialEndsAt !== null && (
          <Text size="caption" tone="subtle" as="span">
            trial ends {new Date(data.trialEndsAt).toLocaleDateString()}
          </Text>
        )}
        {data.dunningAt !== null && (
          <Text size="caption" tone="danger" as="span">
            in dunning since {new Date(data.dunningAt).toLocaleString()}
          </Text>
        )}
      </div>

      {data.catalog === null && data.plan !== "free" && (
        <Alert tone="info">
          <AlertTitle>Not a catalogue plan</AlertTitle>
          <AlertDescription>
            <Mono>{data.plan}</Mono> is not a plan in the catalogue, so its entitlements were set by
            hand rather than by a plan. That is what a design partner looks like.
          </AlertDescription>
        </Alert>
      )}

      <Text size="sm" tone="muted">
        {data.maxio.customerId === null && data.maxio.subscriptionId === null ? (
          "No Maxio account is attached. Plan and entitlements are whatever has been set here."
        ) : (
          <>
            {/* Rendered as plain text when billing is unconfigured: a link built
                from a missing site subdomain goes nowhere, and an operator will
                click it before they read it. */}
            {data.maxio.customerId !== null && (
              <>
                Maxio customer{" "}
                {data.maxio.customerUrl !== null ? (
                  <a
                    href={data.maxio.customerUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="lg:text-primary lg:hover:underline"
                  >
                    {data.maxio.customerId}
                  </a>
                ) : (
                  <Mono>{data.maxio.customerId}</Mono>
                )}
              </>
            )}
            {data.maxio.subscriptionId !== null && (
              <>
                {data.maxio.customerId !== null ? " · " : ""}subscription{" "}
                {data.maxio.subscriptionUrl !== null ? (
                  <a
                    href={data.maxio.subscriptionUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="lg:text-primary lg:hover:underline"
                  >
                    {data.maxio.subscriptionId}
                  </a>
                ) : (
                  <Mono>{data.maxio.subscriptionId}</Mono>
                )}
              </>
            )}
          </>
        )}
      </Text>

      {override !== null && (
        <Alert tone="warning">
          <AlertTitle>Entitlements adjusted by support</AlertTitle>
          <AlertDescription>
            <p>
              {formatLayer(override.entitlements)}
              {override.expiresAt !== null
                ? ` · ${override.inForce ? "until" : "expired"} ${new Date(override.expiresAt).toLocaleString()}`
                : " · until somebody lifts it"}
              {override.actorEmail !== null ? ` · ${override.actorEmail}` : ""}
            </p>
            <p className="lg:mt-1">{override.reason}</p>
          </AlertDescription>
        </Alert>
      )}

      {me.can.administer ? (
        <div className="lg:flex lg:flex-wrap lg:items-start lg:gap-3">
          <OverrideForm orgId={orgId} current={override} onDone={invalidate} />
          {override !== null && (
            <ReasonAction
              label="Lift the override"
              hint="Puts the organisation back on what its plan says. The record of the override is kept."
              onSubmit={async (why) => {
                await api.clearOverride(orgId, why);
                invalidate();
              }}
            />
          )}
        </div>
      ) : (
        <Text size="sm" tone="muted">
          Adjusting entitlements is an administrator action, because it gives an account more than
          it is paying for.
        </Text>
      )}

      <H2 className="lg:text-h4 lg:mt-2">Billing events</H2>
      <DataTable
        columns={BILLING_EVENT_COLUMNS}
        data={data.events}
        caption="What the billing provider has sent about this organisation"
        empty={
          <EmptyState
            icon={<Inbox />}
            title="Nothing from the billing provider"
            description="No webhook has arrived for this organisation."
            glow={false}
          />
        }
      />

      {data.history.length > 0 && (
        <>
          <H2 className="lg:text-h4 lg:mt-2">Override history</H2>
          <DataTable
            columns={OVERRIDE_HISTORY_COLUMNS}
            data={data.history}
            caption="Every entitlement override applied to this organisation"
          />
        </>
      )}
    </section>
  );
}

const BILLING_EVENT_COLUMNS: DataTableColumn<BillingEvent>[] = [
  {
    accessorKey: "receivedAt",
    header: "Received",
    sortFn: "datetime",
    cell: (info) => (
      <Mono className="lg:text-caption lg:text-muted-foreground">
        {new Date(info.row.original.receivedAt).toLocaleString()}
      </Mono>
    ),
  },
  {
    accessorKey: "eventType",
    header: "Event",
    cell: (info) => <Mono className="lg:text-caption">{info.row.original.eventType}</Mono>,
  },
  {
    accessorKey: "subscriptionState",
    header: "Subscription state",
    cell: (info) =>
      info.row.original.subscriptionState ?? <span className="lg:text-subtle">not stated</span>,
  },
  {
    id: "applied",
    header: "Applied",
    cell: (info) => {
      const event = info.row.original;
      if (event.error !== null) {
        return (
          <Badge tone="danger" title={event.error}>
            Failed
          </Badge>
        );
      }
      return event.processedAt !== null ? (
        <Badge tone="success">Applied</Badge>
      ) : (
        <Badge tone="neutral">Pending</Badge>
      );
    },
  },
];

const OVERRIDE_HISTORY_COLUMNS: DataTableColumn<OverrideHistoryRow>[] = [
  {
    accessorKey: "createdAt",
    header: "Applied",
    sortFn: "datetime",
    cell: (info) => (
      <Mono className="lg:text-caption lg:text-muted-foreground">
        {new Date(info.row.original.createdAt).toLocaleString()}
      </Mono>
    ),
  },
  {
    accessorKey: "actorEmail",
    header: "By",
    cell: (info) => (
      <Mono className="lg:text-caption lg:text-muted-foreground">
        {info.row.original.actorEmail ?? "unknown"}
      </Mono>
    ),
  },
  {
    id: "change",
    header: "Change",
    cell: (info) => formatLayer(info.row.original.entitlements),
  },
  { accessorKey: "reason", header: "Reason" },
  {
    id: "state",
    header: "State",
    cell: (info) =>
      info.row.original.revokedAt !== null ? (
        <Badge
          tone="neutral"
          {...(info.row.original.revokedReason ? { title: info.row.original.revokedReason } : {})}
        >
          Lifted
        </Badge>
      ) : (
        <Badge tone="success">Live</Badge>
      ),
  },
];

/**
 * Apply or replace an override.
 *
 * Two steps like every other action on this surface, and the reason field is
 * the one that says who asked for it. The expiry is optional and offered
 * anyway: an override applied during an incident should almost always have one,
 * and a field somebody has to think about is the cheapest way to make them.
 *
 * Applying a new override **replaces** the one in force rather than merging
 * with it, which is what the server does; the form is pre-filled with the
 * current keys so that is what an operator sees.
 */
function OverrideForm({
  orgId,
  current,
  onDone,
}: {
  orgId: string;
  current: EntitlementOverride | null;
  onDone: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<OverrideRow[]>(() => initialRows(current));
  const [expiresAt, setExpiresAt] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () =>
      api.overrideEntitlements(orgId, {
        entitlements: layerFrom(rows),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setOpen(false);
      setReason("");
      setProblem(null);
      onDone();
    },
  });

  const title = current === null ? "Adjust entitlements" : "Replace the override";

  const confirm = (): void => {
    if (Object.keys(layerFrom(rows)).length === 0) {
      setProblem("Set at least one entitlement, or lift the override instead.");
      return;
    }
    if (reason.trim().length < 3) {
      setProblem("Say who asked for this and why.");
      return;
    }
    setProblem(null);
    run.mutate();
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setOpen(true);
        }}
      >
        {title}
      </Button>

      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setProblem(null);
        }}
        title={title}
        description="Sets these keys on top of whatever the plan says, and leaves every other key to the plan. This never touches Maxio: what the customer is charged does not change."
        confirmLabel={run.isPending ? "Working…" : "Apply"}
        onConfirm={confirm}
        pending={run.isPending}
      >
        <div className="lg:flex lg:flex-col lg:gap-4">
          <div className="lg:flex lg:flex-col lg:gap-2">
            {rows.map((row, index) => (
              // Keyed on the row's own id rather than its position, so removing
              // the first row does not make React reuse its input for the
              // second one.
              <div key={row.id} className="lg:flex lg:items-center lg:gap-2">
                <Select
                  value={row.key}
                  onValueChange={(next) => {
                    setRows(replaceAt(rows, index, { ...row, key: next }));
                  }}
                >
                  <SelectTrigger aria-label="Entitlement">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OVERRIDABLE.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {kindOf(row.key) === "boolean" ? (
                  <Select
                    value={row.value === "" ? "false" : row.value}
                    onValueChange={(next) => {
                      setRows(replaceAt(rows, index, { ...row, value: next }));
                    }}
                  >
                    <SelectTrigger aria-label="Value">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="number"
                    min={0}
                    value={row.value}
                    aria-label="Value"
                    onChange={(event) => {
                      setRows(replaceAt(rows, index, { ...row, value: event.target.value }));
                    }}
                  />
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRows(rows.filter((_, i) => i !== index));
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}

            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRows([...rows, { id: nextRowId(), key: nextKey(rows), value: "" }]);
                }}
              >
                Add an entitlement
              </Button>
            </div>
          </div>

          <FormField
            label="Expires at"
            hint="Leave empty for an open-ended override, which is what a design partner wants. Incident relief should have a date: an override with one lifts itself."
          >
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => {
                setExpiresAt(event.target.value);
              }}
            />
          </FormField>

          <FormField
            label="Reason"
            required
            hint="Recorded in the audit trail, and shown to the organisation."
            {...(problem === null ? {} : { error: problem })}
          >
            <Input
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </FormField>

          {run.error && (
            <Text size="sm" tone="danger" role="alert">
              {run.error.message}
            </Text>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}

/** One line of the override form. `id` exists only to key the list. */
interface OverrideRow {
  id: number;
  key: string;
  value: string;
}

let rowIds = 0;

function nextRowId(): number {
  rowIds += 1;
  return rowIds;
}

function kindOf(key: string): "number" | "boolean" {
  return OVERRIDABLE.find((option) => option.key === key)?.kind ?? "number";
}

function nextKey(rows: { key: string }[]): string {
  const used = new Set(rows.map((row) => row.key));
  return OVERRIDABLE.find((option) => !used.has(option.key))?.key ?? DEFAULT_OVERRIDE_KEY;
}

function initialRows(current: EntitlementOverride | null): OverrideRow[] {
  const blank = [{ id: nextRowId(), key: DEFAULT_OVERRIDE_KEY, value: "" }];
  if (current === null) return blank;
  const rows = Object.entries(current.entitlements).map(([key, value]) => ({
    id: nextRowId(),
    key,
    value: String(value),
  }));
  return rows.length > 0 ? rows : blank;
}

function replaceAt<T>(rows: T[], index: number, row: T): T[] {
  return rows.map((existing, i) => (i === index ? row : existing));
}

/**
 * The rows as the API wants them.
 *
 * A row with a blank value is dropped rather than sent as zero: an operator who
 * added a row and changed their mind should not silently set a quota to nothing.
 * The last row for a key wins, so re-selecting a key cannot send it twice.
 */
function layerFrom(rows: OverrideRow[]): Record<string, number | boolean> {
  const layer: Record<string, number | boolean> = {};
  for (const row of rows) {
    if (row.value === "") continue;
    if (kindOf(row.key) === "boolean") {
      layer[row.key] = row.value === "true";
      continue;
    }
    const value = Number(row.value);
    if (Number.isFinite(value) && value >= 0) layer[row.key] = value;
  }
  return layer;
}

/** "Sites 100, MCP publishing true". The console's shorthand for a layer. */
function formatLayer(layer: Record<string, number | boolean>): string {
  const parts = Object.entries(layer).map(([key, value]) => {
    const label = OVERRIDABLE.find((option) => option.key === key)?.label ?? key;
    return `${label} ${String(value)}`;
  });
  return parts.length === 0 ? "no keys" : parts.join(", ");
}
